import { factorFromMarginPct } from '@timelog/pm-shared';

/**
 * Parse helpers for the Financials inline cells — pure string → number, kept
 * free of React/provider imports so unit tests run in the node environment.
 */

/**
 * Amount input → whole currency units. Accepts da-DK typing: "1.200.000",
 * "1200000", "1,2m"/"1.2M" (million shorthand mirrors the abbreviated
 * display). Null = unparsable (callers ignore the commit).
 */
export const parseAmountInput = (raw: string): number | null => {
  const text = raw.trim().toLowerCase();
  if (text === '') return null;

  const million = text.match(/^(-?\d+(?:[.,]\d+)?)\s*m$/);
  if (million) {
    const value = Number(million[1]!.replace(',', '.'));
    return Number.isFinite(value) && value >= 0 ? Math.round(value * 1_000_000) : null;
  }

  const digits = text.replace(/[.\s]/g, '').replace(',', '.');
  const value = Number(digits);
  return Number.isFinite(value) && value >= 0 ? Math.round(value) : null;
};

/** Factor input → positive number ("2,8", "×2.8", "2.8"). Null = unparsable. */
export const parseFactorInput = (raw: string): number | null => {
  const text = raw.trim().replace('×', '').replace('x', '').replace(',', '.');
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) && value > 0 ? value : null;
};

/** Hours input → non-negative number, 2dp ("396", "12,5"). Null = unparsable. */
export const parseHoursInput = (raw: string): number | null => {
  const text = raw.trim().replace(/\./g, '').replace(',', '.');
  if (text === '') return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100) / 100;
};

/**
 * Margin input → a FACTOR ("52", "52%", "64,3%"), for the margin display metric
 * (ADR 0027): stored values are always factors, so the conversion happens here
 * on commit — factor = 1/(1 − m), margin clamped at 95%.
 */
export const parseMarginInput = (raw: string): number | null => {
  const text = raw.trim().replace('%', '').replace(',', '.');
  if (text === '') return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return null;
  return factorFromMarginPct(value);
};

/** Percent input → 0..100 ("15", "15%", "12,5%"). Null = unparsable. */
export const parsePctInput = (raw: string): number | null => {
  const text = raw.trim().replace('%', '').replace(',', '.');
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
};
